/* eslint-disable complexity */
import {
	CheckPermissionProps,
	CommunityPermissionOptions,
	EventPermissionOptions,
	getCommunityAdminPermissions,
	getCommunityPermissions,
	getEventPermissions,
	HasCommunityAdminPermissionOptions,
	HasEventRevenueReadPermissionOptions,
	ProfileOptions,
} from '@sweatpals/shared';
import { merge } from 'lodash-es';
import { useMemo } from 'react';

export enum PermissionTypeNameEnum {
	Events = 'events',
	Revenue = 'revenue',
	Overview = 'overview',
	Orders = 'orders',
	Waitlist = 'waitlist',
	Wallet = 'wallet',
	Perks = 'perks',
	Settings = 'settings',
	Integrations = 'integrations',
	Memberships = 'memberships',
	CommunityAdmin = 'communityAdmin',
	Community = 'community',
	CampaignBlast = 'campaignBlast',
	CommunityPerks = 'communityPerks',
}

export type CRUDActions = 'create' | 'read' | 'edit' | 'delete';

export type EventsAction = CRUDActions | 'readTicketsCount' | 'readOverview' | 'duplicate' | 'checkIn' | 'editTickets' | 'readStats';
export type CommunityAction = CRUDActions;
export type CommunityAdminAction = CRUDActions;
export type MembershipsAction = CRUDActions;
export type OverviewAction = 'read';
export type RevenueAction = 'read';
export type OrdersAction = 'read';
export type WaitlistAction = 'read';
export type WalletAction = 'read';
export type PerksAction = CRUDActions;
export type SettingsAction = 'read';
export type IntegrationsAction = 'read' | 'create' | 'edit' | 'delete';
export type CampaignBlastAction = 'read';
export type CommunityPerksAction = 'read';

export type AllActions =
	| EventsAction
	| CommunityAdminAction
	| RevenueAction
	| OverviewAction
	| WaitlistAction
	| OrdersAction
	| WalletAction
	| PerksAction
	| MembershipsAction
	| SettingsAction
	| IntegrationsAction
	| CampaignBlastAction
	| CommunityPerksAction;

// TODO - make this type better
export type PermissionType =
	| string
	| {
			type: PermissionTypeNameEnum.Overview;
			action: OverviewAction;
	  }
	| {
			type: PermissionTypeNameEnum.Events;
			action: EventsAction;
	  }
	| {
			type: PermissionTypeNameEnum.Community;
			action: CommunityAction;
	  }
	| {
			type: PermissionTypeNameEnum.Revenue;
			action: RevenueAction;
	  }
	| {
			type: PermissionTypeNameEnum.Memberships;
			action: MembershipsAction;
	  }
	| {
			type: PermissionTypeNameEnum.Orders;
			action: OrdersAction;
	  }
	| {
			type: PermissionTypeNameEnum.Waitlist;
			action: WaitlistAction;
	  }
	| {
			type: PermissionTypeNameEnum.Wallet;
			action: WalletAction;
	  }
	| {
			type: PermissionTypeNameEnum.Perks;
			action: PerksAction;
		}
	| {
			type: PermissionTypeNameEnum.Settings;
			action: SettingsAction;
	  }
	| {
			type: PermissionTypeNameEnum.Integrations;
			action: IntegrationsAction;
	  }
	| {
			type: PermissionTypeNameEnum.CommunityAdmin;
			action: CommunityAdminAction;
	  }
	| {
			type: PermissionTypeNameEnum.CampaignBlast;
			action: CampaignBlastAction;
	  }
	| {
			type: PermissionTypeNameEnum.CommunityPerks;
			action: CommunityPerksAction;
		};

// TODO: make this type more specific
export type PermissionOptions = Partial<
	| CommunityPermissionOptions
	| HasCommunityAdminPermissionOptions
	| HasEventRevenueReadPermissionOptions
	| ProfileOptions
	| EventPermissionOptions
>;

// TODO: make this type more specific
export type HasPermissionsResult = Partial<Record<PermissionTypeNameEnum, Partial<Record<AllActions, boolean>>>>;

export const useHasPermissions = (
	permissionTypes: PermissionType[], // TODO: currently untyped, should be typed
	props: CheckPermissionProps,
	options: PermissionOptions = {}
): HasPermissionsResult => {
	const permissions = useMemo(() => {
		const permissions = {
			[PermissionTypeNameEnum.Events]: getEventPermissions(props),
			[PermissionTypeNameEnum.CommunityAdmin]: getCommunityAdminPermissions(props),
			[PermissionTypeNameEnum.Community]: getCommunityPermissions(props),
		};

		return permissions;
	}, [props]);

	const hasPermissions = useMemo<HasPermissionsResult>(() => {
		if (!permissionTypes?.length || !props) return {};

		// TODO: remove this any cast
		const anyOptions = (options ?? {}) as any;

		return merge(
			{},
			...permissionTypes.map((permission) => {
				const [permissionType, permissionAction] =
					typeof permission === 'string' ? permission.split('.') : [permission.type, permission.action];

				const makeResult = (verdict?: boolean): Partial<HasPermissionsResult> => ({
					[permissionType]: {
						[permissionAction]: verdict ?? false,
					},
				});

				/* MAP PERMISSION TYPE TO UTIL */

				if (permissionType === PermissionTypeNameEnum.Events) {
					const eventPermissions = permissions.events;

					if (!eventPermissions) return makeResult(false);

					switch (permissionAction) {
						case 'create':
						case 'duplicate':
							return makeResult(eventPermissions.canCreate({ ...anyOptions }));
						case 'read':
							return makeResult(eventPermissions.canRead({ ...anyOptions }));
						case 'edit':
							return makeResult(eventPermissions.canEdit({ ...anyOptions }));
						case 'delete':
							return makeResult(eventPermissions.canDelete({ ...anyOptions }));
						case 'checkIn':
							return makeResult(eventPermissions.canEditCheckIn({ ...anyOptions }));
						case 'editTickets':
							return makeResult(eventPermissions.canEditTickets({ ...anyOptions }));
						case 'readStats':
							return makeResult(eventPermissions.canReadStats({ ...anyOptions }));
						case 'readTicketsCount':
							return makeResult(eventPermissions.canReadTicketsCount({ ...anyOptions }));
						case 'readOverview':
							return makeResult(eventPermissions.canReadEventOverview({ ...anyOptions }));
						default:
							return makeResult(false);
					}
				}

				if (permissionType === PermissionTypeNameEnum.Revenue) {
					const eventPermissions = permissions.events;

					if (!eventPermissions) return makeResult(false);

					switch (permissionAction) {
						case 'read':
							return makeResult(eventPermissions.canReadRevenue({ ...anyOptions }));
						default:
							return makeResult(false);
					}
				}

				if (permissionType === PermissionTypeNameEnum.Waitlist) {
					const eventPermissions = permissions.events;

					if (!eventPermissions) return makeResult(false);

					switch (permissionAction) {
						case 'read':
							return makeResult(eventPermissions.canReadWaitlist({ ...anyOptions }));
						default:
							return makeResult(false);
					}
				}

				if (permissionType === PermissionTypeNameEnum.CampaignBlast) {
					const communityPermissions = permissions.community;

					if (!communityPermissions) return makeResult(false);

					switch (permissionAction) {
						case 'read':
							return makeResult(communityPermissions.canReadCampaignBlast({ ...anyOptions }));
						default:
							return makeResult(false);
					}
				}

				if (permissionType === PermissionTypeNameEnum.CommunityPerks) {
					const communityPermissions = permissions.community;

					if (!communityPermissions) return makeResult(false);

					switch (permissionAction) {
						case 'read':
							return makeResult(communityPermissions.canReadPerks({ ...anyOptions }));
						default:
							return makeResult(false);
					}
				}

				if (permissionType === PermissionTypeNameEnum.Overview) {
					const communityPermissions = permissions.community;

					if (!communityPermissions) return makeResult(false);

					switch (permissionAction) {
						case 'read':
							return makeResult(communityPermissions.canReadOverview({ ...anyOptions }));
						default:
							return makeResult(false);
					}
				}

				if (permissionType === PermissionTypeNameEnum.Memberships) {
					const communityPermissions = permissions.community;

					if (!communityPermissions) return makeResult(false);

					switch (permissionAction) {
						case 'create':
						case 'read':
						case 'edit':
						case 'delete':
							return makeResult(communityPermissions.canReadMembership({ ...anyOptions }));
						default:
							return makeResult(false);
					}
				}

				if (permissionType === PermissionTypeNameEnum.Orders) {
					const communityPermissions = permissions.community;

					if (!communityPermissions) return makeResult(false);

					switch (permissionAction) {
						case 'read':
							return makeResult(communityPermissions.canReadOrders({ ...anyOptions }));
						default:
							return makeResult(false);
					}
				}

				if (permissionType === PermissionTypeNameEnum.Wallet) {
					const communityPermissions = permissions.community;

					if (!communityPermissions) return makeResult(false);

					switch (permissionAction) {
						case 'read':
							return makeResult(communityPermissions.canReadWallet({ ...anyOptions }));
						default:
							return makeResult(false);
					}
				}

				if (permissionType === PermissionTypeNameEnum.Perks) {
					const communityPermissions = permissions.community;

					if (!communityPermissions) return makeResult(false);

					switch (permissionAction) {
						case 'create':
						case 'read':
						case 'edit':
						case 'delete':
							return makeResult(communityPermissions.canReadPerks({ ...anyOptions }));
						default:
							return makeResult(false);
					}
				}

				if (permissionType === PermissionTypeNameEnum.Settings) {
					const communityPermissions = permissions.community;

					if (!communityPermissions) return makeResult(false);

					switch (permissionAction) {
						case 'read':
							return makeResult(communityPermissions.canReadSettings({ ...anyOptions }));
						default:
							return makeResult(false);
					}
				}

				if (permissionType === PermissionTypeNameEnum.Integrations) {
					const communityPermissions = permissions.community;

					if (!communityPermissions) return makeResult(false);

					switch (permissionAction) {
						case 'read':
						case 'create':
						case 'edit':
						case 'delete':
							return makeResult(communityPermissions.canReadIntegrations({ ...anyOptions }));
						default:
							return makeResult(false);
					}
				}

				if (permissionType === PermissionTypeNameEnum.CommunityAdmin) {
					const communityAdminPermissions = permissions.communityAdmin;

					if (!communityAdminPermissions) return makeResult(false);

					switch (permissionAction) {
						case 'create':
							return makeResult(communityAdminPermissions.canAdd({ ...anyOptions }));
						case 'read':
							return makeResult(communityAdminPermissions.canRead({ ...anyOptions }));
						case 'edit':
							return makeResult(communityAdminPermissions.canEdit({ ...anyOptions }));
						case 'delete':
							return makeResult(communityAdminPermissions.canDelete({ ...anyOptions }));
						default:
							return makeResult(false);
					}
				}

				if (permissionType === PermissionTypeNameEnum.Community) {
					const communityPermissions = permissions.community;

					if (!communityPermissions) return makeResult(false);

					switch (permissionAction) {
						case 'create':
							return makeResult(communityPermissions.canCreate({ ...anyOptions }));
						case 'edit':
							return makeResult(communityPermissions.canEdit({ ...anyOptions }));
						default:
							return makeResult(false);
					}
				}

				// If no permission type is matched, return false
				return makeResult(false);
			})
		);
	}, [permissionTypes, props, permissions, options]);

	return hasPermissions;
};
